import miningImg from "@/assets/case-studies/mining-safety.png";
import supplyImg from "@/assets/case-studies/global-supply-chain.jpg";

/** Inline text: plain strings or course links. */
export type Inline = string | { text: string; to: string };

export type CaseBlock =
  | { t: "h2"; x: string }
  | { t: "h3"; x: string }
  | { t: "p"; x: Inline[] }
  | { t: "quote"; x: string }
  | { t: "ul"; items: string[] };

export interface CaseStudy {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  headline: string;
  intro?: string;
  image: string;
  imageAlt: string;
  facts: { label: string; value: string }[];
  blocks: CaseBlock[];
  ctaTitle: string;
  ctaLabel: string;
  // Listing card
  kind: "Enterprise" | "Individual";
  cardEyebrow: string;
  cardTitle: string;
  cardBody: string;
  cardFacts: { label: string; value: string }[];
}

export const caseStudiesIndex = {
  metaTitle: "Leadership Training Case Studies | Leadership Edge Live",
  metaDescription:
    "Real results from real clients. See how a Fortune 500 supply chain team and a mining safety specialist developed stronger leaders with Leadership Edge Live.",
  eyebrow: "Case Studies",
  headline: "Leadership Development That Works: Case Studies from Real Clients",
  intro:
    "Two perspectives on the same work — an enterprise transformation inside a Fortune 500 supply chain, and the personal growth of a safety specialist on a 12-hour shift.",
  ctaTitle: "See what Leadership Edge Live can do for your organization",
  ctaBody:
    "Book a consultation with our team to explore a leadership program tailored to your industry, your roles, and your business outcomes.",
  ctaLabel: "Schedule a consultation",
};

export const caseStudies: CaseStudy[] = [
  {
    slug: "leadership-development-global-supply-chain",
    metaTitle: "Supply Chain Leadership Training | Leadership Edge Live",
    metaDescription:
      "A Fortune 500 company with 300+ supply chain employees used LEL to close skill gaps, ease stakeholder tension, and build personalized development plans.",
    headline:
      "How One Fortune 500 Corporation Built Stronger Leaders Across Its Entire Supply Chain",
    image: supplyImg,
    imageAlt: "Global supply chain leadership team",
    facts: [
      { label: "Company", value: "Fortune 500" },
      { label: "Department", value: "Global Supply Chain" },
      { label: "Scope", value: "300+ employees" },
      { label: "Method", value: "WRAP methodology" },
    ],
    blocks: [
      { t: "h2", x: "Overview" },
      {
        t: "p",
        x: [
          "A leading global corporation, recognized as a Fortune 500 company, faced significant challenges within its expansive Global Supply Chain department. With over 300 employees and a complex organizational structure that included seven layers of management, the department was struggling with a lack of strategic focus on essential leadership skills.",
        ],
      },
      {
        t: "p",
        x: [
          "These challenges were further compounded by growing tensions with internal and external stakeholders, which began to affect production results. To address these issues, the corporation partnered with us to implement a comprehensive talent development initiative aimed at aligning leadership skills with organizational needs.",
        ],
      },
      { t: "h2", x: "Challenges" },
      { t: "p", x: ["The Global Supply Chain department encountered two primary challenges:"] },
      { t: "h3", x: "Lack of Focus on Leadership Skills" },
      {
        t: "p",
        x: [
          "The organization’s strategy did not adequately address the essential skills needed by leaders to succeed in their roles.",
        ],
      },
      { t: "h3", x: "Stakeholder Tension" },
      {
        t: "p",
        x: [
          "Strained relationships with internal and external stakeholders were negatively impacting production outcomes.",
        ],
      },
      { t: "h2", x: "Our Approach" },
      { t: "h3", x: "Skill Identification:" },
      {
        t: "ul",
        items: [
          "Collaborated closely with the organization to identify the critical skills required for success in each leadership role within the department.",
          "Conducted comprehensive assessments covering critical behaviors: self-awareness, self-regulation, empathy, motivation, and social skills.",
          "Facilitated personalized, one-on-one coaching conversations to help employees define their personal development goals.",
        ],
      },
      { t: "h3", x: "Clarification and Facilitation:" },
      {
        t: "ul",
        items: [
          "Held in-depth, clarifying conversations with key stakeholders to ensure leadership strategies addressed both individual development needs and the broader organizational objectives.",
          "Integrated the WRAP methodology, a continuous learning model that integrates reinforcement techniques, to ensure that leadership development continued long after the program’s conclusion.",
        ],
      },
      { t: "h3", x: "Personalized Development Plans" },
      {
        t: "ul",
        items: [
          "Provided personalized course recommendations to each employee, tailored to their specific role, identified skill gaps, and personal development goals.",
          "Equipped managers with tools and guidance to facilitate meaningful feedback and coaching conversations with their teams.",
          "Instituted an annual development plan review process to ensure leadership growth remains aligned with evolving organizational goals and individual professional growth.",
        ],
      },
      { t: "h2", x: "Impact" },
      {
        t: "p",
        x: [
          "Participants across all seven management layers reported stronger confidence in difficult conversations, with direct feedback indicating improved communication with both internal teams and external supply chain partners.",
        ],
      },
      {
        t: "quote",
        x: "I tend to get wrapped up in my emotions and let them control me when in a stressful situation, personal or professional setting. [LEL] provided me with tools to refer to when I’m in those situations going forward, to better manage my emotions, slow things down, and be proactive in getting through it.",
      },
      {
        t: "quote",
        x: "I learned about the four base personality types and how to work with each. I felt that this will empower me to relate better to others and build stronger teams.",
      },
      {
        t: "quote",
        x: "I have learned how to prioritize tasks by effectively communicating with peers and supervisors.",
      },
      { t: "h2", x: "Conclusion" },
      {
        t: "p",
        x: [
          "Through a strategic focus on personalized development and thorough skill gap analysis, this global corporation is on a path to strengthening its leadership capabilities and improving production outcomes. While it is still early in the process, the feedback from participants underscores the value of investing in targeted talent development solutions.",
        ],
      },
      {
        t: "p",
        x: [
          "If you lead a large organization with complex management layers and need a structured, scalable approach to leadership development, this is exactly what we built this program for. Schedule a consultation to see how we can do the same for your team.",
        ],
      },
    ],
    ctaTitle: "",
    ctaLabel: "Schedule a consultation",
    kind: "Enterprise",
    cardEyebrow: "Fortune 500 Global Corporation",
    cardTitle: "Strengthening Leadership in a Global Corporation",
    cardBody:
      "Inside a 300-person Global Supply Chain department with seven layers of management, we built personalized development plans, ran one-on-one coaching, and embedded the WRAP methodology to align leadership skills with the business.",
    cardFacts: [
      { label: "Company", value: "Fortune 500" },
      { label: "Department", value: "Global Supply Chain" },
      { label: "Scope", value: "300+ employees" },
      { label: "Method", value: "WRAP methodology" },
    ],
  },
  {
    slug: "leadership-training-mining-safety",
    metaTitle: "Leadership Training for Mining & Safety | Leadership Edge Live",
    metaDescription:
      "A Freeport-McMoRan safety specialist used LEL training to build influence and improve communication while working 12-hour shifts in a high-pressure mine.",
    headline: "How a Mining Safety Specialist Used Leadership Training to Lead With More Influence",
    intro:
      "The mining and safety industry demands a specific kind of leader: someone who can enforce standards under pressure while still earning trust from their team. Leadership Edge Live has worked with Freeport-McMoRan since 2009 to develop exactly that kind of capability.",
    image: miningImg,
    imageAlt:
      "Safety specialist at a mining company developing leadership skills through professional training",
    facts: [
      { label: "Client since", value: "2009" },
      { label: "Role", value: "Safety Specialist" },
      { label: "Industry", value: "Mining, Safety" },
      { label: "Courses Completed", value: "20+ since July 2024" },
    ],
    blocks: [
      { t: "h2", x: "A Veteran’s Transition to Influence" },
      {
        t: "p",
        x: [
          "Twelve-hour shifts, tight timelines, and a high-stakes, low-margin-for-error environment—Franus Tomkiewicz is used to pressure. Born and raised in Detroit, Franus entered the military when college felt out of reach.",
        ],
      },
      {
        t: "p",
        x: [
          "Today, he serves as one of twelve Safety Specialists at Freeport-McMoRan’s Safford mine, with a clear mandate: keep people safe, ensure performance, and lead by example. Freeport-McMoRan has been a long-standing partner of Leadership Edge Live (LEL), providing its employees with access to professional development programs. Although Franus initially enrolled to complete two required courses, he quickly found himself coming back for more.",
        ],
      },
      {
        t: "quote",
        x: "I can honestly say every LEL course I have experienced has benefited me tremendously, both on a professional and personal level.",
      },
      { t: "h2", x: "From Taskmaster to Team-Builder" },
      {
        t: "p",
        x: [
          "Franus is a checklist-driven, high-standards kind of teammate. He thrives on clarity and action. What he hadn’t expected was that managing safety had just as much to do with people as it did with process. That is where LEL began to shift his perspective.",
        ],
      },
      {
        t: "p",
        x: [
          "Two of the most challenging courses for him were ",
          { text: "The Art of Influence", to: "/course-catalog/the-art-of-influence/" },
          " and ",
          {
            text: "Navigating Organizational Politics",
            to: "/course-catalog/navigating-organizational-politics/",
          },
          ". Coming from a military background, adjusting to corporate ambiguity and subtle social dynamics wasn’t easy. Direct feedback didn’t always land, and coworkers avoided deeper conversations.",
        ],
      },
      {
        t: "quote",
        x: "Influencing others or navigating organizational politics can be challenging for me at times due to my initiative-taking personality.",
      },
      {
        t: "p",
        x: [
          "Fortunately, LEL’s ",
          {
            text: "4P Personality Styles and Influence Without Authority",
            to: "/course-catalog/improve-communication-through-the-4p-personality-styles/",
          },
          " courses gave him the frameworks needed to better understand those around him and adjust his approach to connect more effectively.",
        ],
      },
      {
        t: "quote",
        x: "Understanding and adjusting yourself to the various personalities and thought processes of those around you plays an important role in your future success.",
      },
      {
        t: "p",
        x: [
          "And what further deepened that self-awareness were the required Insight Reports embedded in several LEL courses. These reports offer constructive and positive feedback gathered from colleagues who know you well, as well as those who don’t.",
        ],
      },
      {
        t: "p",
        x: [
          "For Franus, and countless other learners, the insights they provide are eye-opening. They clarify the improvements needed for continuous personal and professional development, like how a direct tone can unintentionally shut down dialogue. This was a turning point for Franus—it gave him the language to adjust how he speaks and listens on the job.",
        ],
      },
      { t: "h2", x: "Growth on His Terms" },
      {
        t: "p",
        x: [
          "Franus’ evolution is defined by a blend of humility, structure, and growth mindset. With insights gained through LEL, he now approaches conversations more thoughtfully, even when it isn’t easy to do so in ever-evolving, high-stress environments.",
        ],
      },
      {
        t: "p",
        x: [
          "Today, Franus continues to engage with LEL while juggling 12-hour shifts, travel, and a clear goal to advance within the company. The flexibility to revisit recorded sessions has made continued growth practical and sustainable.",
        ],
      },
      {
        t: "p",
        x: [
          'When asked what kind of course he would design based on what’s helped him most, his answer was immediate: “Influencing Effectively Using Greater Initiative and Performance Standards." And every day, he puts that philosophy into practice.',
        ],
      },
    ],
    ctaTitle:
      "Do you lead a team in a similarly high-stakes environment? See how Leadership Edge Live helps companies develop stronger, more influential leaders at every level.",
    ctaLabel: "Schedule a conversation",
    kind: "Individual",
    cardEyebrow: "Franus Tomkiewicz · Freeport-McMoRan",
    cardTitle: "How a Safety Specialist at Freeport-McMoRan Is Expanding His Impact with LEL",
    cardBody:
      "A military veteran turned Safety Specialist at the Safford mine, Franus came for two required courses and stayed for twenty. Insight Reports and the 4P Personality Styles reshaped how he influences crews on 12-hour shifts.",
    cardFacts: [
      { label: "Role", value: "Safety Specialist" },
      { label: "Employer", value: "Freeport-McMoRan" },
      { label: "Client since", value: "2009" },
      { label: "Courses completed", value: "20+ since Jul 2024" },
    ],
  },
];

export const caseStudyBySlug = (slug: string) => caseStudies.find((c) => c.slug === slug);
